import React, { useState, useEffect, Component, useRef } from 'react';
import axios from '../../config/axiosConfig';
import { MdEdit, MdDelete, MdSearch, MdDownload, MdPictureAsPdf } from 'react-icons/md';
import { FaWhatsapp, FaFilePdf } from 'react-icons/fa';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ReceiptTemplate from './ReceiptTemplate';

class ReceiptsErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }
    
    componentDidCatch(error, errorInfo) {
        this.setState({
            hasError: true,
            error: error,
            errorInfo: errorInfo
        });
        console.error("Receipts Error:", error, errorInfo);
    }
    
    render() {
        if (this.state.hasError) {
            return (
                <div className="p-8 m-8 bg-red-50 border border-red-200 rounded text-left">
                    <h2 className="text-xl font-bold text-red-700 mb-4">Receipts Component Crashed</h2>
                    <p className="font-semibold">{this.state.error && this.state.error.toString()}</p>
                    <pre className="mt-4 text-xs bg-red-100 p-4 rounded overflow-auto whitespace-pre-wrap">
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </pre>
                </div>
            );
        }
        return this.props.children;
    }
}

const ReceiptsInner = ({ view, onFYClick, preSelectedFilters, onBack }) => {
    const tableRef = useRef(null);
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Selection and Filter States
    const [selectedReceipts, setSelectedReceipts] = useState([]);
    const [advancedFilters, setAdvancedFilters] = useState({
        dateFrom: '',
        dateTo: '',
        receiptNo: '',
        memberName: '',
        type: '',
        status: ''
    });

    // PDF Generation State
    const [pdfReceipt, setPdfReceipt] = useState(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const pdfRef = useRef();

    const feeCategoryOptions = [
        { label: 'Membership Fee', value: 'registration' },
        { label: 'Leadership Fee', value: 'leadership' },
        { label: 'Renewal Fee', value: 'renewal' },
        { label: 'Donation', value: 'donation' }
    ];

    const paymentStatusTypeOptions = [
        { label: 'Full Payment', value: 'full' },
        { label: 'Part Payment', value: 'part' }
    ];

    const paymentModeOptions = [
        'Cash',
        'UPI',
        'Bank Transfer',
        'Credit/Debit Card',
        'Cheque',
        'Other'
    ];

    // Edit Modal State
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingReceipt, setEditingReceipt] = useState(null);
    const [editFormData, setEditFormData] = useState({
        payment_type: 'registration',
        payment_status_type: 'full',
        amount: '',
        total_amount: '',
        balance_amount: '',
        payment_date: '',
        status: 'success',
        transaction_id: '',
        payment_mode: 'UPI',
        notes: ''
    });

    // Create Modal State
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [memberSearchQuery, setMemberSearchQuery] = useState('');
    const [memberSuggestions, setMemberSuggestions] = useState([]);
    const [createFormData, setCreateFormData] = useState({
        member: '',
        member_name: '',
        member_address: '',
        member_mobile: '',
        payment_type: 'registration',
        payment_status_type: 'full',
        amount: '',
        total_amount: '',
        balance_amount: '',
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        status: 'success',
        transaction_id: '',
        payment_mode: 'UPI',
        notes: ''
    });

    const [stats, setStats] = useState({ fy_stats: [], monthly_stats: [] });
    const [showDashboard, setShowDashboard] = useState(true);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
 
    const statusColors = {
        success: 'bg-green-100 text-green-800',
        pending: 'bg-yellow-100 text-yellow-800',
        failed: 'bg-red-100 text-red-800'
    };

    const fetchReceipts = async () => {
        try {
            setLoading(true);
            const response = await axios.get('nai-members/payments/');
            if (Array.isArray(response.data)) {
                setReceipts(response.data);
            } else if (response.data && Array.isArray(response.data.results)) {
                setReceipts(response.data.results);
            } else {
                setReceipts([]);
                console.error("Unexpected API response format for receipts:", response.data);
            }
        } catch (error) {
            console.error("Error fetching receipts:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get('nai-members/payment-stats/');
            setStats(response.data);
        } catch (error) {
            console.error("Error fetching payment stats:", error);
        }
    };

    useEffect(() => {
        fetchReceipts();
        fetchStats();
    }, []);

    useEffect(() => {
        if (preSelectedFilters) {
            setAdvancedFilters(prev => ({
                ...prev,
                ...preSelectedFilters
            }));
            // If we are in list view, search directly
            fetchReceipts();
        }
    }, [preSelectedFilters]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleAdvancedFilterChange = (e) => {
        setAdvancedFilters({
            ...advancedFilters,
            [e.target.name]: e.target.value
        });
    };

    const filteredReceipts = (receipts || []).filter(receipt => {
        // Global Search
        const query = (searchQuery || '').toLowerCase();
        const matchesGlobal = (
            (receipt.member_name && String(receipt.member_name).toLowerCase().includes(query)) ||
            (receipt.member_id_str && String(receipt.member_id_str).toLowerCase().includes(query)) ||
            (receipt.transaction_id && String(receipt.transaction_id).toLowerCase().includes(query))
        );
        if (!matchesGlobal) return false;

        // Advanced Column Filters
        if (advancedFilters.receiptNo && !(receipt.receipt_number || '').toLowerCase().includes(advancedFilters.receiptNo.toLowerCase())) return false;
        if (advancedFilters.memberName && !(receipt.member_name || '').toLowerCase().includes(advancedFilters.memberName.toLowerCase())) return false;
        if (advancedFilters.type && receipt.payment_type !== advancedFilters.type) return false;
        if (advancedFilters.status && receipt.status !== advancedFilters.status) return false;
        
        if (advancedFilters.dateFrom || advancedFilters.dateTo) {
            const rowDate = new Date(receipt.payment_date);
            if (advancedFilters.dateFrom && rowDate < new Date(advancedFilters.dateFrom)) return false;
            if (advancedFilters.dateTo && rowDate > new Date(advancedFilters.dateTo)) return false;
        }

        return true;
    });

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedReceipts(filteredReceipts.map(r => r.id));
        } else {
            setSelectedReceipts([]);
        }
    };

    const handleSelectOne = (e, id) => {
        if (e.target.checked) {
            setSelectedReceipts([...selectedReceipts, id]);
        } else {
            setSelectedReceipts(selectedReceipts.filter(selectedId => selectedId !== id));
        }
    };

    const safeFormatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '-';
            return format(date, 'dd MMM yyyy');
        } catch (e) {
            return '-';
        }
    };

    const handleExportCSV = () => {
        const receiptsToExport = selectedReceipts.length > 0 
            ? filteredReceipts.filter(r => selectedReceipts.includes(r.id))
            : filteredReceipts;

        if (!receiptsToExport || receiptsToExport.length === 0) {
            alert("No receipts to export.");
            return;
        }
        const headers = ["receipt_number", "payment_date", "member_name", "member_id_str", "payment_type", "amount", "status", "transaction_id", "payment_mode"];
        const rows = receiptsToExport.map(row => headers.map(header => {
            let val = row[header];
            if (val === null || val === undefined) return '""';
            const str = String(val);
            if (str.includes(',') || str.includes('\n') || str.includes('"')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        }).join(","));
        
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `receipts_export_${format(new Date(), 'yyyyMMdd_HHmm')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleWhatsAppShare = (row) => {
        if (!row.member_mobile) {
            alert("No mobile number available to send WhatsApp.");
            return;
        }
        const message = `Dear ${row.member_name},\n\nThank you for your payment of Rs. ${row.amount} for ${row.payment_type} on ${safeFormatDate(row.payment_date)}.\nReceipt No: ${row.receipt_number || 'Auto-generated'}\nStatus: ${row.status.toUpperCase()}\n\nRegards,\nNAI India`;
        const encodedMessage = encodeURIComponent(message);
        
        // Strip out non-numeric chars
        let phone = String(row.member_mobile).replace(/\D/g, '');
        // Naive assumption for India; usually 10 digits
        if (phone.length === 10) phone = `91${phone}`;
        
        window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
    };

    const handleDownloadPDF = async (row) => {
        setPdfReceipt(row);
        setIsGeneratingPdf(true);
        
        // Wait for state to update and component to render
        setTimeout(async () => {
            if (!pdfRef.current) {
                setIsGeneratingPdf(false);
                return;
            }
            try {
                const canvas = await html2canvas(pdfRef.current, { scale: 2, useCORS: true });
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                pdf.save(`Receipt_${row.receipt_number || row.id}.pdf`);
            } catch (error) {
                console.error("Error generating PDF", error);
                alert("Failed to generate PDF.");
            } finally {
                setPdfReceipt(null);
                setIsGeneratingPdf(false);
            }
        }, 300); // 300ms delay to ensure React commits the DOM update
    };

    const handleEditClick = (receipt) => {
        setEditingReceipt(receipt);
        setEditFormData({
            payment_type: receipt.payment_type || 'registration',
            payment_status_type: receipt.payment_status_type || 'full',
            amount: receipt.amount || '',
            total_amount: receipt.total_amount || '',
            balance_amount: receipt.balance_amount || '',
            payment_date: receipt.payment_date || '',
            status: receipt.status || 'success',
            transaction_id: receipt.transaction_id || '',
            payment_mode: receipt.payment_mode || 'UPI',
            notes: receipt.notes || ''
        });
        setIsEditModalOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'payment_status_type') {
                if (value === 'full') {
                    updated.total_amount = '';
                    updated.balance_amount = '';
                } else {
                    const tot = parseFloat(updated.total_amount) || 0;
                    const amt = parseFloat(updated.amount) || 0;
                    updated.balance_amount = updated.total_amount ? Math.max(0, tot - amt).toFixed(2) : '';
                }
            } else if (name === 'total_amount' || name === 'amount') {
                if (updated.payment_status_type === 'part') {
                    const tot = name === 'total_amount' ? (parseFloat(value) || 0) : (parseFloat(updated.total_amount) || 0);
                    const amt = name === 'amount' ? (parseFloat(value) || 0) : (parseFloat(updated.amount) || 0);
                    const hasTotal = name === 'total_amount' ? !!value : !!updated.total_amount;
                    updated.balance_amount = hasTotal ? Math.max(0, tot - amt).toFixed(2) : '';
                }
            }
            return updated;
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...editFormData,
                amount: parseFloat(editFormData.amount),
                total_amount: editFormData.payment_status_type === 'part' && editFormData.total_amount ? parseFloat(editFormData.total_amount) : null,
                balance_amount: editFormData.payment_status_type === 'part' && editFormData.balance_amount ? parseFloat(editFormData.balance_amount) : null,
            };
            await axios.patch(`nai-members/payments/${editingReceipt.id}/`, payload);
            setIsEditModalOpen(false);
            fetchReceipts(); // Refresh data
            fetchStats();
        } catch (error) {
            console.error("Error updating receipt:", error);
            alert("Failed to update receipt.");
        }
    };

    const handleCreateClick = () => {
        setCreateFormData({
            member: '',
            member_name: '',
            member_address: '',
            member_mobile: '',
            payment_type: 'registration',
            payment_status_type: 'full',
            amount: '',
            total_amount: '',
            balance_amount: '',
            payment_date: format(new Date(), 'yyyy-MM-dd'),
            status: 'success',
            transaction_id: '',
            payment_mode: 'UPI',
            notes: ''
        });
        setMemberSearchQuery('');
        setMemberSuggestions([]);
        setIsCreateModalOpen(true);
    };

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'payment_status_type') {
                if (value === 'full') {
                    updated.total_amount = '';
                    updated.balance_amount = '';
                } else {
                    const tot = parseFloat(updated.total_amount) || 0;
                    const amt = parseFloat(updated.amount) || 0;
                    updated.balance_amount = updated.total_amount ? Math.max(0, tot - amt).toFixed(2) : '';
                }
            } else if (name === 'total_amount' || name === 'amount') {
                if (updated.payment_status_type === 'part') {
                    const tot = name === 'total_amount' ? (parseFloat(value) || 0) : (parseFloat(updated.total_amount) || 0);
                    const amt = name === 'amount' ? (parseFloat(value) || 0) : (parseFloat(updated.amount) || 0);
                    const hasTotal = name === 'total_amount' ? !!value : !!updated.total_amount;
                    updated.balance_amount = hasTotal ? Math.max(0, tot - amt).toFixed(2) : '';
                }
            }
            return updated;
        });
    };

    const handleMemberSearch = async (e) => {
        const query = e.target.value;
        setMemberSearchQuery(query);
        if (query.length > 2) {
            try {
                const response = await axios.get(`nai-members/members/?search=${query}`);
                setMemberSuggestions(response.data.results || response.data);
            } catch (error) {
                console.error("Error searching members:", error);
            }
        } else {
            setMemberSuggestions([]);
        }
    };

    const handleSelectMember = (member) => {
        setCreateFormData({
            ...createFormData,
            member: member.id,
            member_name: member.member_name,
            member_address: member.member_address,
            member_mobile: member.mobile
        });
        setMemberSearchQuery(member.member_name);
        setMemberSuggestions([]);
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        if (!createFormData.member) {
            alert("Please select a member first.");
            return;
        }
        try {
            const payload = {
                ...createFormData,
                amount: parseFloat(createFormData.amount),
                total_amount: createFormData.payment_status_type === 'part' && createFormData.total_amount ? parseFloat(createFormData.total_amount) : null,
                balance_amount: createFormData.payment_status_type === 'part' && createFormData.balance_amount ? parseFloat(createFormData.balance_amount) : null,
            };
            await axios.post('nai-members/payments/', payload);
            setIsCreateModalOpen(false);
            fetchReceipts();
            fetchStats();
        } catch (error) {
            console.error("Error creating receipt:", error);
            alert("Failed to generate receipt.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this receipt?")) {
            try {
                await axios.delete(`nai-members/payments/${id}/`);
                fetchReceipts();
            } catch (error) {
                console.error("Error deleting receipt:", error);
                alert("Failed to delete receipt.");
            }
        }
    };

    const handleImportClick = () => {
        setIsImportModalOpen(true);
    };

    const handleImportSubmit = async (e) => {
        e.preventDefault();
        if (!importFile) return;

        const formData = new FormData();
        formData.append('file', importFile);

        setImporting(true);
        try {
            const response = await axios.post('nai-members/payments/import_csv/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert(response.data.message);
            setIsImportModalOpen(false);
            fetchReceipts();
            fetchStats();
        } catch (error) {
            console.error("Error importing payments:", error);
            alert("Failed to import payments.");
        } finally {
            setImporting(false);
        }
    };

    const handleFYCardClick = (fyLabel) => {
        const [startYear, endYear] = fyLabel.split('-');
        const filters = {
            dateFrom: `${startYear}-04-01`,
            dateTo: `${endYear}-03-31`
        };

        if (onFYClick) {
            onFYClick(filters);
        } else {
            setAdvancedFilters({
                ...advancedFilters,
                ...filters
            });
            // Fallback scroll if no navigation provided
            setTimeout(() => {
                tableRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading Receipts...</div>;

    return (
        <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    {view === 'list' && onBack && (
                        <button 
                            onClick={onBack}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            title="Back to Dashboard"
                        >
                            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                    )}
                    <h2 className="text-xl font-bold text-gray-800">
                        {view === 'dashboard' ? 'Receipts Dashboard' : 'Payment Receipts'}
                    </h2>
                </div>
                
                {view === 'list' && (
                    <div className="flex w-full sm:w-auto gap-4">
                        <div className="relative flex-1 sm:w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MdSearch className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search Name or Transaction ID..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>
                        <button
                            onClick={handleCreateClick}
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 gap-2"
                        >
                            <MdPictureAsPdf className="w-5 h-5" /> Generate Receipt
                        </button>
                        <button
                            onClick={handleImportClick}
                            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 gap-2"
                        >
                            <MdDownload className="w-5 h-5 text-blue-600" /> Import Past Years
                        </button>
                        <button
                            onClick={handleExportCSV}
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 gap-2"
                        >
                            <MdDownload className="w-5 h-5" /> Export
                        </button>
                    </div>
                )}
            </div>

            {view === 'dashboard' && stats.fy_stats.length > 0 && (
                <div className="p-6 bg-gray-50 border-b border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-700">Financial Performance Overview</h3>
                        <button 
                            onClick={() => setShowDashboard(false)}
                            className="text-xs text-blue-600 hover:underline"
                        >
                            Hide Stats
                        </button>
                    </div>
                    
                    {/* FY Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {stats.fy_stats.map((fy) => (
                            <div 
                                key={fy.label}
                                onClick={() => handleFYCardClick(fy.label)}
                                className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer group"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">FY {fy.label}</span>
                                    <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                                        <MdPictureAsPdf className="text-blue-600 w-5 h-5" />
                                    </div>
                                </div>
                                <div className="text-2xl font-bold text-gray-800">
                                    ₹{Number(fy.total_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                    {fy.count} Receipts Generated
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Simple SVG Graph */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-6">Payment Trends (Last 3 Years)</h4>
                        <div className="relative h-64 w-full">
                            <div className="absolute inset-0 flex items-end justify-between px-2 gap-1 h-48 border-b border-gray-100">
                                {stats.monthly_stats.length > 0 ? stats.monthly_stats.slice(-12).map((m, i) => {
                                    const maxAmount = Math.max(...stats.monthly_stats.map(x => x.amount)) || 1;
                                    const height = (m.amount / maxAmount) * 100;
                                    return (
                                        <div key={i} className="flex-1 flex flex-col items-center group relative h-full justify-end">
                                            <div 
                                                className="w-full max-w-[40px] bg-blue-500 group-hover:bg-blue-600 rounded-t-sm transition-all duration-500"
                                                style={{ height: `${height}%` }}
                                            >
                                                <div className="opacity-0 group-hover:opacity-100 absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] py-1 px-2 rounded whitespace-nowrap z-10">
                                                    ₹{m.amount.toLocaleString()}
                                                </div>
                                            </div>
                                            <div className="mt-2 text-[10px] text-gray-400 font-medium rotate-45 origin-left whitespace-nowrap">
                                                {m.month}
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div className="w-full text-center text-gray-300 italic pt-20">Insufficient data for graph</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {view === 'list' && (
                <div className="overflow-x-auto" ref={tableRef}>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left">
                                <input type="checkbox" onChange={handleSelectAll} checked={selectedReceipts.length === filteredReceipts.length && filteredReceipts.length > 0} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                        {/* Filter Row */}
                        <tr className="bg-white border-b border-gray-200">
                            <th className="px-6 py-2"></th>
                            <th className="px-6 py-2">
                                <div className="flex gap-1 flex-col">
                                    <input type="date" name="dateFrom" value={advancedFilters.dateFrom} onChange={handleAdvancedFilterChange} className="w-full text-xs box-border p-1 border rounded" />
                                    <input type="date" name="dateTo" value={advancedFilters.dateTo} onChange={handleAdvancedFilterChange} className="w-full text-xs box-border p-1 border rounded" />
                                </div>
                            </th>
                            <th className="px-6 py-2">
                                <input type="text" name="receiptNo" placeholder="Filter..." value={advancedFilters.receiptNo} onChange={handleAdvancedFilterChange} className="w-full text-xs p-1 border rounded" />
                            </th>
                            <th className="px-6 py-2">
                                <input type="text" name="memberName" placeholder="Filter Name..." value={advancedFilters.memberName} onChange={handleAdvancedFilterChange} className="w-full text-xs p-1 border rounded" />
                            </th>
                            <th className="px-6 py-2">
                                <select name="type" value={advancedFilters.type} onChange={handleAdvancedFilterChange} className="w-full text-xs p-1 border rounded">
                                    <option value="">All</option>
                                    <option value="registration">Membership</option>
                                    <option value="leadership">Leadership</option>
                                    <option value="renewal">Renewal</option>
                                    <option value="donation">Donation</option>
                                </select>
                            </th>
                            <th className="px-6 py-2"></th>
                            <th className="px-6 py-2">
                                <select name="status" value={advancedFilters.status} onChange={handleAdvancedFilterChange} className="w-full text-xs p-1 border rounded">
                                    <option value="">All</option>
                                    <option value="pending">Pending</option>
                                    <option value="success">Success</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </th>
                            <th className="px-6 py-2"></th>
                            <th className="px-6 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredReceipts.length > 0 ? (
                            filteredReceipts.map((row) => (
                                <tr key={row.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input type="checkbox" checked={selectedReceipts.includes(row.id)} onChange={(e) => handleSelectOne(e, row.id)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {safeFormatDate(row.payment_date)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                                        {row.receipt_number || <span className="text-gray-300 italic">Not generated</span>}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{row.member_name}</div>
                                        <div className="text-xs text-gray-500">ID: {row.member_id_str || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col gap-1 items-start">
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 uppercase">
                                                {row.payment_type === 'registration' ? 'Membership' : row.payment_type}
                                            </span>
                                            {row.payment_status_type === 'part' && (
                                                <span className="px-2 py-0.2 text-[10px] leading-4 font-semibold rounded bg-amber-100 text-amber-800">
                                                    Part Payment
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                        <div>₹{Number(row.amount || 0).toFixed(2)}</div>
                                        {row.payment_status_type === 'part' && row.balance_amount && (
                                            <div className="text-[11px] text-red-600 font-semibold">Bal: ₹{row.balance_amount}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[row.status] || 'bg-gray-100 text-gray-800'}`}>
                                            {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Unknown'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {row.transaction_id || <span className="text-gray-300 italic">None provided</span>}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3 flex justify-end">
                                        <button onClick={() => handleDownloadPDF(row)} className="text-gray-500 hover:text-gray-900" title="Download PDF Receipt">
                                            <FaFilePdf className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => handleWhatsAppShare(row)} className="text-green-500 hover:text-green-700" title="Send message via WhatsApp">
                                            <FaWhatsapp className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => handleEditClick(row)} className="text-blue-600 hover:text-blue-900" title="Edit">
                                            <MdEdit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => handleDelete(row.id)} className="text-red-600 hover:text-red-900" title="Delete">
                                            <MdDelete className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                                    No receipts found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            )}

            {/* Create Receipt Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" onClick={() => setIsCreateModalOpen(false)}>
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                            <form onSubmit={handleCreateSubmit}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 className="text-lg leading-6 font-bold text-gray-900 mb-4 text-center border-b pb-2">Generate New Receipt</h3>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="col-span-1 md:col-span-2 relative">
                                            <label className="block text-sm font-medium text-gray-700">Search Member <span className="text-red-500">*</span></label>
                                            <div className="relative mt-1">
                                                <input 
                                                    type="text" 
                                                    placeholder="Type name or ID to search..." 
                                                    value={memberSearchQuery}
                                                    onChange={handleMemberSearch}
                                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    required
                                                />
                                                {memberSuggestions.length > 0 && (
                                                    <div className="absolute z-10 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm mt-1">
                                                        {memberSuggestions.map((member) => (
                                                            <div 
                                                                key={member.id}
                                                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-600 hover:text-white"
                                                                onClick={() => handleSelectMember(member)}
                                                            >
                                                                <div className="font-semibold">{member.member_name}</div>
                                                                <div className="text-xs text-gray-400 group-hover:text-blue-100">ID: {String(member.member_id).padStart(4, '0')} | Mobile: {member.mobile}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 p-3 rounded col-span-1 md:col-span-2 text-sm grid grid-cols-1 md:grid-cols-3 gap-2 border border-gray-200">
                                            <div>
                                                <span className="block text-gray-500 font-bold uppercase text-[10px]">Member Name</span>
                                                <span className="text-gray-900 font-medium">{createFormData.member_name || 'Not Selected'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500 font-bold uppercase text-[10px]">Mobile</span>
                                                <span className="text-gray-900 font-medium">{createFormData.member_mobile || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500 font-bold uppercase text-[10px]">Address</span>
                                                <span className="text-gray-900 font-medium truncate" title={createFormData.member_address}>{createFormData.member_address || '-'}</span>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Fee Category / Type <span className="text-red-500">*</span></label>
                                            <select name="payment_type" value={createFormData.payment_type} onChange={handleCreateChange} className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                                {feeCategoryOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Payment Type <span className="text-red-500">*</span></label>
                                            <select name="payment_status_type" value={createFormData.payment_status_type} onChange={handleCreateChange} className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                                {paymentStatusTypeOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Part Payment vs Full Payment in Create */}
                                        {createFormData.payment_status_type === 'part' ? (
                                            <div className="col-span-1 md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-amber-50 rounded-md border border-amber-200">
                                                <div>
                                                    <label className="block text-xs font-semibold text-amber-900">Total Amount (₹) *</label>
                                                    <input 
                                                        type="number" 
                                                        step="0.01" 
                                                        name="total_amount" 
                                                        value={createFormData.total_amount} 
                                                        onChange={handleCreateChange} 
                                                        placeholder="e.g. 5000"
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2.5 bg-white text-sm focus:ring-blue-500 focus:border-blue-500" 
                                                        required 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-amber-900">Amount Paid (₹) *</label>
                                                    <input 
                                                        type="number" 
                                                        step="0.01" 
                                                        name="amount" 
                                                        value={createFormData.amount} 
                                                        onChange={handleCreateChange} 
                                                        placeholder="e.g. 2000"
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2.5 bg-white text-sm focus:ring-blue-500 focus:border-blue-500" 
                                                        required 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-amber-900">Balance Amount (₹)</label>
                                                    <input 
                                                        type="text" 
                                                        readOnly
                                                        value={createFormData.balance_amount !== '' ? `₹ ${createFormData.balance_amount}` : '-'} 
                                                        className="mt-1 block w-full bg-amber-100 text-amber-900 font-bold border border-amber-300 rounded-md shadow-sm py-1.5 px-2.5 text-sm cursor-not-allowed" 
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Amount Paid (₹) <span className="text-red-500">*</span></label>
                                                <input 
                                                    type="number" 
                                                    step="0.01" 
                                                    name="amount" 
                                                    value={createFormData.amount} 
                                                    onChange={handleCreateChange} 
                                                    placeholder="e.g. 1000"
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" 
                                                    required 
                                                />
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Payment Date <span className="text-red-500">*</span></label>
                                            <input type="date" name="payment_date" value={createFormData.payment_date} onChange={handleCreateChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Mode of Payment <span className="text-red-500">*</span></label>
                                            <select name="payment_mode" value={createFormData.payment_mode || 'UPI'} onChange={handleCreateChange} className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                                {paymentModeOptions.map(mode => (
                                                    <option key={mode} value={mode}>{mode}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Status</label>
                                            <select name="status" value={createFormData.status} onChange={handleCreateChange} className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                                <option value="success">Success</option>
                                                <option value="pending">Pending</option>
                                                <option value="failed">Failed</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Transaction ID / Reference No</label>
                                            <input type="text" name="transaction_id" value={createFormData.transaction_id} onChange={handleCreateChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Optional" />
                                        </div>

                                        <div className="col-span-1 md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700">Notes / Remarks</label>
                                            <textarea name="notes" value={createFormData.notes} onChange={handleCreateChange} rows="2" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"></textarea>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
                                        Generate Receipt
                                    </button>
                                    <button type="button" onClick={() => setIsCreateModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" onClick={() => setIsEditModalOpen(false)}>
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                            <form onSubmit={handleEditSubmit}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="border-b pb-3 mb-4">
                                        <h3 className="text-lg leading-6 font-bold text-gray-900">
                                            Edit Receipt #{editingReceipt?.id}
                                        </h3>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Member: <span className="font-semibold text-gray-800">{editingReceipt?.member_name}</span> (ID: {editingReceipt?.member_id_str || 'N/A'})
                                        </p>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Fee Category */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Fee Category / Member Type <span className="text-red-500">*</span>
                                            </label>
                                            <select 
                                                name="payment_type" 
                                                value={editFormData.payment_type} 
                                                onChange={handleEditChange} 
                                                className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            >
                                                {feeCategoryOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Payment Type */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Payment Type <span className="text-red-500">*</span>
                                            </label>
                                            <select 
                                                name="payment_status_type" 
                                                value={editFormData.payment_status_type} 
                                                onChange={handleEditChange} 
                                                className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            >
                                                {paymentStatusTypeOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Part Payment vs Full Payment */}
                                        {editFormData.payment_status_type === 'part' ? (
                                            <div className="col-span-1 md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-amber-50 rounded-md border border-amber-200">
                                                <div>
                                                    <label className="block text-xs font-semibold text-amber-900">Total Amount (₹) *</label>
                                                    <input 
                                                        type="number" 
                                                        step="0.01" 
                                                        name="total_amount" 
                                                        value={editFormData.total_amount} 
                                                        onChange={handleEditChange} 
                                                        placeholder="e.g. 5000"
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2.5 bg-white text-sm focus:ring-blue-500 focus:border-blue-500" 
                                                        required 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-amber-900">Amount Paid (₹) *</label>
                                                    <input 
                                                        type="number" 
                                                        step="0.01" 
                                                        name="amount" 
                                                        value={editFormData.amount} 
                                                        onChange={handleEditChange} 
                                                        placeholder="e.g. 2000"
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2.5 bg-white text-sm focus:ring-blue-500 focus:border-blue-500" 
                                                        required 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-amber-900">Balance Amount (₹)</label>
                                                    <input 
                                                        type="text" 
                                                        readOnly
                                                        value={editFormData.balance_amount !== '' ? `₹ ${editFormData.balance_amount}` : '-'} 
                                                        className="mt-1 block w-full bg-amber-100 text-amber-900 font-bold border border-amber-300 rounded-md shadow-sm py-1.5 px-2.5 text-sm cursor-not-allowed" 
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Amount Paid (₹) <span className="text-red-500">*</span></label>
                                                <input 
                                                    type="number" 
                                                    step="0.01" 
                                                    name="amount" 
                                                    value={editFormData.amount} 
                                                    onChange={handleEditChange} 
                                                    placeholder="e.g. 1000"
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" 
                                                    required 
                                                />
                                            </div>
                                        )}

                                        {/* Payment Date */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Payment Date <span className="text-red-500">*</span></label>
                                            <input 
                                                type="date" 
                                                name="payment_date" 
                                                value={editFormData.payment_date} 
                                                onChange={handleEditChange} 
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" 
                                                required 
                                            />
                                        </div>

                                        {/* Mode of Payment */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Mode of Payment <span className="text-red-500">*</span></label>
                                            <select 
                                                name="payment_mode" 
                                                value={editFormData.payment_mode || 'UPI'} 
                                                onChange={handleEditChange} 
                                                className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            >
                                                {paymentModeOptions.map(mode => (
                                                    <option key={mode} value={mode}>{mode}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Status */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Payment Status</label>
                                            <select 
                                                name="status" 
                                                value={editFormData.status} 
                                                onChange={handleEditChange} 
                                                className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            >
                                                <option value="success">Success</option>
                                                <option value="pending">Pending</option>
                                                <option value="failed">Failed</option>
                                            </select>
                                        </div>

                                        {/* Transaction ID */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Transaction ID / Reference No</label>
                                            <input 
                                                type="text" 
                                                name="transaction_id" 
                                                value={editFormData.transaction_id} 
                                                onChange={handleEditChange} 
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" 
                                                placeholder="e.g. UPI Ref / Cheque No" 
                                            />
                                        </div>

                                        {/* Notes */}
                                        <div className="col-span-1 md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700">Notes / Remarks</label>
                                            <textarea 
                                                name="notes" 
                                                value={editFormData.notes} 
                                                onChange={handleEditChange} 
                                                rows="2" 
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                placeholder="Optional notes..."
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
                                        Save Changes
                                    </button>
                                    <button type="button" onClick={() => setIsEditModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden PDF Template Container */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                {pdfReceipt && <ReceiptTemplate receipt={pdfReceipt} ref={pdfRef} />}
            </div>
            {isGeneratingPdf && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl flex items-center gap-4 text-gray-800 text-lg font-semibold">
                        <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path></svg>
                        Generating High-Quality PDF Engine...
                    </div>
                </div>
            )}
            {/* Import Modal */}
            {isImportModalOpen && (
                <div className="fixed inset-0 z-[60] overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsImportModalOpen(false)}></div>
                        <div className="relative bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Import Past Year Payments</h3>
                            <form onSubmit={handleImportSubmit}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Select CSV File</label>
                                    <input 
                                        type="file" 
                                        accept=".csv"
                                        onChange={(e) => setImportFile(e.target.files[0])}
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        required
                                    />
                                    <p className="mt-2 text-xs text-gray-500">Format: member_id, payment_type, amount, payment_date, status, transaction_id, payment_mode, notes</p>
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button 
                                        type="button" 
                                        onClick={() => setIsImportModalOpen(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        disabled={importing}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {importing ? 'Importing...' : 'Upload & Import'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Receipts = (props) => (
    <ReceiptsErrorBoundary>
        <ReceiptsInner {...props} />
    </ReceiptsErrorBoundary>
);

export default Receipts;
